import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ConfirmDialog from '../components/ConfirmDialog';
import { RxDatabaseProvider } from 'rxdb/plugins/react';
import { removeRxDatabase } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { isStagedFault } from '../features/support/devFaults';
import { getDatabase, releaseOpenGuard, type AppDatabase } from './database';
import {
    canStartNextGeneration,
    classifyStall,
    currentDatabaseName,
    hasFreshAbandonedOpen,
    startNextDatabaseGeneration,
    type StallKind
} from './openStall';
import { setupReplication } from './replication';
import { startMediaUploader } from '../features/media/mediaUploader';
import { requestPersistentStorage } from '../utils/persistentStorage';
import { startQueueSelfHeal } from '../features/sync/pendingQueue';
import { useAuth } from '../features/auth/useAuth';
import {
    formatBootTrace,
    markBoot,
    readBootTrace,
    readPreviousBootTrace
} from '../utils/bootTrace';
import {
    checkDataEpoch,
    markEpochAccepted,
    onEpochMismatch
} from '../utils/dataEpoch';
import {
    reportDatabaseProblem,
    startReportSender,
    type ReportState
} from '../features/support/problemReport';
import { pendingLocalChanges } from './authorJournal';
import {
    downloadSalvage,
    salvageUnsent,
    wipeRawDatabases,
    type SalvageResult
} from '../features/support/salvage';
import { listPendingIds } from '../features/media/blobStore';

/**
 * How long the database may take before we say something is wrong - NOT a
 * deadline: the open keeps running, and the app still starts if it finishes.
 * Measured on real devices, a cold first start can exceed twelve seconds, and
 * after a schema bump a reload waits for the old connection to let go. Only a
 * rejection is reported as a failure - that one has an error to show.
 */
const STALL_NOTICE_MS = 25_000;

/**
 * Shorter when the last start was interrupted while the database was opening:
 * that leaves a connection nothing can reach, and no amount of waiting frees it
 * (db/openStall.ts). Long enough that a device which merely restarted quickly
 * still gets its chance to open normally.
 */
const BLOCKED_NOTICE_MS = 6000;

/**
 * How long the blocked screen waits before helping itself. Long enough that
 * somebody holding the tablet can read it and press the button (or close the
 * window, which is still the cheapest fix); short enough that a kiosk tablet
 * nobody is standing at comes back on its own.
 */
const AUTO_RECOVER_MS = 30_000;

/**
 * Whether this device could fetch anything right now. A fresh local copy is
 * only an answer with a connection: without one the app would come back empty,
 * which is worse than the screen it replaces. The work is not lost either way -
 * it sits in the copy that is being left behind.
 */
function isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine;
}

/** `?fault=…` is somebody looking at a screen, not a device in trouble. */
const STAGED_NOTICE_MS = 1500;

/** How long a normal open may take before we bother showing a spinner. */
const SPINNER_DELAY_MS = 600;

/** How long the wipe may take before the reload happens anyway. */
const RESET_TIMEOUT_MS = 5000;

/** The width every paragraph on these screens is measured for, in pixels. */
const TEXT_WIDTH = 520;

/** The centred single column that all of the start-up screens share. */
const SCREEN_SX = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    p: 3,
    textAlign: 'center'
} as const;

/**
 * Wipe the local RxDB (Dexie) storage and reload - the recovery action when the
 * database will not open, e.g. a half-applied schema migration.
 *
 * `removeRxDatabase` goes through RxDB's internal store, which after an
 * interrupted start never calls back at all: not an error, not a rejection,
 * simply nothing. So it gets five seconds, then the stores are deleted by name
 * (`wipeRawDatabases`) and the page reloads whatever happened.
 */
async function resetLocalData() {
    try {
        await Promise.race([
            /**
             * Its own catch, not the one below: once the timer wins the race
             * nothing is awaiting this any more, and a rejection arriving later
             * would surface as an unhandled one - noise in exactly the
             * diagnostics this release exists to keep clean.
             */
            removeRxDatabase(currentDatabaseName(), getRxStorageDexie()).catch(
                () => {}
            ),
            new Promise((resolve) => setTimeout(resolve, RESET_TIMEOUT_MS))
        ]);
    } catch {
        // Best effort - the fallback below and the reload happen regardless.
    }
    await wipeRawDatabases();
    // Our own reload: no "leave site?" dialog, and no breadcrumb blaming the
    // next start for an interruption we caused ourselves.
    releaseOpenGuard();
    location.reload();
}

/**
 * "3 Datensaetze und 2 Fotos" - only the halves that are actually there, and
 * counted, because "einige" is exactly the word that makes somebody press the
 * button anyway. Put together from three messages rather than one nested
 * plural: the catalogue stays readable, and each language keeps its own word
 * order.
 */
function describeUnsent(
    intl: IntlShape,
    recordCount: number,
    photos: number
): string {
    const records = intl.formatMessage(
        { id: 'db.unsent.records' },
        { count: recordCount }
    );
    const photoCount = intl.formatMessage(
        { id: 'db.unsent.photos' },
        { count: photos }
    );
    if (recordCount > 0 && photos > 0) {
        return intl.formatMessage(
            { id: 'db.unsent.both' },
            { records, photos: photoCount }
        );
    }
    return recordCount > 0 ? records : photoCount;
}

/**
 * What the rescue achieved, in one sentence somebody can act on - honest about
 * the half that did not go, which now exists only in the file and on this
 * device. Photos are counted separately because they travel separately: they
 * are uploaded where they belong rather than written into the file.
 */
function describeRescue(intl: IntlShape, result: SalvageResult): string {
    const translate = (id: string, values?: Record<string, number>) =>
        intl.formatMessage({ id }, values);
    if (result.found.length === 0 && result.photosFound === 0) {
        return translate('db.rescue.nothing');
    }

    const photosSent = result.photosFound - result.photosLeft;
    const sentences: string[] = [];
    if (result.found.length === 0) {
        sentences.push(translate('db.rescue.noRecords'));
    } else if (result.offline) {
        sentences.push(
            translate('db.rescue.offline', { count: result.found.length })
        );
    } else if (result.refused.length === 0) {
        sentences.push(
            translate('db.rescue.uploaded', { count: result.uploaded })
        );
    } else {
        sentences.push(
            translate('db.rescue.partial', {
                uploaded: result.uploaded,
                refused: result.refused.length
            })
        );
    }
    if (photosSent > 0) {
        sentences.push(
            translate('db.rescue.photosSent', { count: photosSent })
        );
    }
    if (result.photosLeft > 0) {
        sentences.push(
            translate('db.rescue.photosStuck', { count: result.photosLeft })
        );
    }
    // The reassurance belongs only where nothing at all stayed behind.
    const nothingLeftBehind =
        !result.offline &&
        result.refused.length === 0 &&
        result.photosLeft === 0;
    if (result.found.length > 0 && nothingLeftBehind) {
        sentences.push(translate('db.rescue.nowFree'));
    }
    return sentences.join(' ');
}

/**
 * A way out that this screen can take by ITSELF, for the faults where reloading
 * is no answer. On a kiosk tablet nobody closes a tab, so the app has to get
 * past the fault on its own - after the rescue has had its turn, never before.
 */
interface NoticeRecovery {
    label: ReactNode;
    run: () => void;
    /** Do it unasked once this long has passed, for an unattended device. */
    automaticAfterMs?: number;
}

/**
 * What a screen may offer. Waiting is its own answer: while the app is still
 * starting, reloading is the very thing that breaks it, so that screen shows no
 * button for it.
 */
interface NoticeActions {
    /** Offer the reload. Off where a reload would cause the fault. */
    reload?: boolean;
}

/**
 * The screen for a database that will not open - both for the open that FAILED
 * and for the one merely taking too long. While it is only slow the open is
 * still running, so the screen must not suggest that anything has to be done.
 *
 * It may use the theme and the catalogues: what is broken here is the local
 * database, and neither of those depends on it. The crash boundary is the one
 * screen that has to do without both - it sits outside them, because either of
 * them may be what broke (see components/AppErrorBoundary).
 */
function DatabaseNotice({
    heading,
    children,
    message,
    recovery,
    actions,
    reported
}: {
    heading: ReactNode;
    children: ReactNode;
    message?: string;
    recovery?: NoticeRecovery;
    actions?: NoticeActions;
    /** What became of the report the app filed, when it filed one. */
    reported?: ReportState | null;
}) {
    const intl = useIntl();
    const offerReload = actions?.reload ?? true;
    const previousTrace = formatBootTrace(readPreviousBootTrace());

    /**
     * What resetting would destroy. Read from the journal in localStorage and
     * from the media store, NOT from RxDB - the database is the one thing not
     * available here, and both of those outlive it.
     *
     * `useState(fn)` rather than a bare call: the count must be the one taken
     * when the screen appeared, not one that moves while it is read.
     */
    const [unsentRecords, setUnsentRecords] = useState(pendingLocalChanges);
    const [unsentPhotos, setUnsentPhotos] = useState(0);
    /** null = not tried, 'running' = under way, otherwise what came of it. */
    const [rescue, setRescue] = useState<'running' | SalvageResult | null>(
        null
    );
    useEffect(() => {
        let live = true;
        void listPendingIds()
            .then((ids) => {
                if (live) {
                    setUnsentPhotos(ids.length);
                }
            })
            .catch(() => {
                // The blob store is unavailable too. Then the records alone
                // carry the warning; a screen for a broken device must not
                // depend on a second store being healthy.
            });
        return () => {
            live = false;
        };
    }, []);

    // Get the unsent work off the device. `automatic` decides one thing: whether
    // a file is forced on somebody. Run by itself it leaves the file behind the
    // button; pressed by hand, a failure hands it over at once, since then it is
    // the only remaining copy that leaves this tablet.
    async function rescueWork(automatic = false) {
        setRescue('running');
        const result = await salvageUnsent();
        if (!automatic && (result.refused.length > 0 || result.offline)) {
            void downloadSalvage(result.found);
        }
        setRescue(result);
        setUnsentRecords(pendingLocalChanges());
        setUnsentPhotos(result.photosLeft);
    }

    /**
     * Try it without being asked, as soon as this screen appears: somebody
     * standing in a trench with an app that will not start reloads, resets or
     * hands the tablet on, and by then the work is gone.
     *
     * It waits for the fault report to go first. The report carries the same
     * documents (see `unsentAttachment`), and a rescue that cleared the journal
     * ahead of it would post an empty attachment - throwing away the copy that
     * survives even a device nobody sees again.
     */
    const [confirmingReset, setConfirmingReset] = useState(false);
    // Once, and only once. A ref rather than state: nothing on screen depends
    // on it - the button reads `rescue`, which says the same thing and more -
    // and a state write here would render the screen a second time before the
    // first one has been seen.
    const triedByItself = useRef(false);
    useEffect(() => {
        if (triedByItself.current || reported === 'sending') {
            return;
        }
        if (unsentRecords + unsentPhotos === 0) {
            return;
        }
        triedByItself.current = true;
        // Starting the rescue is the point of this effect, not a state that
        // could have been worked out while rendering: it hands the work to the
        // server, and `rescue` only says that this has begun. The one render it
        // costs happens while a device with unsent work is being rescued.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void rescueWork(true);
    }, [reported, unsentRecords, unsentPhotos]);

    const atRisk = unsentRecords + unsentPhotos;
    const unsent = describeUnsent(intl, unsentRecords, unsentPhotos);

    /**
     * Take the way out without being asked - but only after the rescue has
     * finished, and only if one was offered at all. A tablet on a wall has
     * nobody to press the button; a tablet in somebody's hands gets to press it
     * sooner, which is why the button is there from the first moment.
     */
    const automaticAfter = recovery?.automaticAfterMs;
    useEffect(() => {
        if (!recovery || automaticAfter === undefined) {
            return;
        }
        const timer = window.setTimeout(() => {
            if (rescue !== 'running') {
                recovery.run();
            }
        }, automaticAfter);
        return () => window.clearTimeout(timer);
        // `rescue` restarts the wait while the rescue is still going: the work
        // on this device leaves first, whatever the clock says.
    }, [recovery, automaticAfter, rescue]);

    /**
     * Never wipe on one click, whatever is on the device. It is the one action
     * here that cannot be taken back, and it ALWAYS asks: a question that
     * appears only sometimes teaches people to click through it, and the one
     * time it does appear is the one that costs a day's work.
     *
     * What the device holds changes the TEXT, not whether it asks - named and
     * counted when work is at risk, plainly harmless when there is none.
     */
    function requestReset() {
        setConfirmingReset(true);
    }

    return (
        <Box sx={SCREEN_SX}>
            <ConfirmDialog
                open={confirmingReset}
                title={
                    <FormattedMessage
                        id={
                            atRisk > 0
                                ? 'db.reset.title.atRisk'
                                : 'db.reset.title.safe'
                        }
                    />
                }
                message={
                    atRisk > 0 ? (
                        <FormattedMessage
                            id="db.reset.message.atRisk"
                            values={{
                                total: atRisk,
                                summary: <strong>{unsent}</strong>
                            }}
                        />
                    ) : (
                        <FormattedMessage id="db.reset.message.safe" />
                    )
                }
                details={
                    atRisk > 0 ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <FormattedMessage id="db.reset.warning" />
                        </Alert>
                    ) : undefined
                }
                confirmLabel={
                    <FormattedMessage
                        id={
                            atRisk > 0
                                ? 'db.reset.confirm.atRisk'
                                : 'db.reset.confirm.safe'
                        }
                    />
                }
                confirmColor={atRisk > 0 ? 'error' : 'primary'}
                onConfirm={() => void resetLocalData()}
                onCancel={() => setConfirmingReset(false)}
            />
            <Typography variant="h5" component="h2">
                {heading}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: TEXT_WIDTH }}>
                {children}
            </Typography>
            {/* Said out loud rather than done quietly: the app has just sent a
                description of this device somewhere, and whoever is holding it
                should be able to read that off the screen. */}
            {reported && reported !== 'off' ? (
                <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ maxWidth: TEXT_WIDTH }}
                >
                    <FormattedMessage
                        id={
                            reported === 'sending'
                                ? 'db.report.sending'
                                : reported === 'sent'
                                  ? 'db.report.sent'
                                  : 'db.report.queued'
                        }
                    />
                </Typography>
            ) : null}
            <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
            >
                {/* The way on, first: the screen's own recovery where there is
                    one, otherwise the retry - and nothing where both would do
                    harm. */}
                {recovery || offerReload ? (
                    <Button
                        variant="contained"
                        disabled={Boolean(recovery) && rescue === 'running'}
                        onClick={
                            recovery
                                ? recovery.run
                                : () => {
                                      releaseOpenGuard();
                                      location.reload();
                                  }
                        }
                    >
                        {recovery ? (
                            recovery.label
                        ) : (
                            <FormattedMessage id="db.action.retry" />
                        )}
                    </Button>
                ) : null}
                <Button
                    variant="outlined"
                    color="inherit"
                    // Not while the rescue is still uploading: the wipe would
                    // take the very records it is in the middle of saving.
                    disabled={rescue === 'running'}
                    onClick={requestReset}
                >
                    <FormattedMessage
                        id={
                            rescue === 'running'
                                ? 'db.action.securing'
                                : 'db.action.refetch'
                        }
                    />
                </Button>
            </Stack>
            {/* What the reset costs, said BEFORE it is pressed and only when it
                would cost anything.
                The screen used to call the local data "nur ein Cache", which is
                true of everything the server already has and false of exactly
                the work that matters: a Befund written in a trench with no
                signal exists on this device and nowhere else. Whoever is
                standing in front of a broken app is also the person least able
                to judge that, so it is counted for them. */}
            {atRisk > 0 ? (
                <Alert
                    severity="warning"
                    sx={{ maxWidth: TEXT_WIDTH, textAlign: 'left' }}
                >
                    <FormattedMessage
                        id="db.unsent.warning"
                        values={{ total: atRisk, summary: unsent }}
                    />
                </Alert>
            ) : null}
            {/* The way out that did not exist before: the documents are still
                readable even now (features/support/salvage), so they can go up
                or into a file BEFORE anything is thrown away. Offered only
                while there is something to save, and it stays offered after a
                partial rescue - the leftovers are exactly what needs a second
                try from a better spot. */}
            {atRisk > 0 ? (
                <Button
                    variant="contained"
                    color="warning"
                    onClick={() => void rescueWork(false)}
                    disabled={rescue === 'running'}
                >
                    <FormattedMessage
                        id={
                            rescue === 'running'
                                ? 'db.rescue.action.running'
                                : rescue
                                  ? 'db.rescue.action.again'
                                  : 'db.rescue.action'
                        }
                    />
                </Button>
            ) : null}
            {rescue && rescue !== 'running' ? (
                <Typography
                    color="text.secondary"
                    sx={{ maxWidth: TEXT_WIDTH }}
                >
                    {describeRescue(intl, rescue)}{' '}
                    <Link
                        component="button"
                        type="button"
                        onClick={() => void downloadSalvage(rescue.found)}
                    >
                        <FormattedMessage id="db.rescue.download" />
                    </Link>
                </Typography>
            ) : null}
            {/* Below the buttons, because it is what to do once they have been
                tried. The report goes to the developers, but nobody standing in
                a trench should be left waiting on that - the office can get
                them working again today. */}
            <Typography color="text.secondary" sx={{ maxWidth: TEXT_WIDTH }}>
                <FormattedMessage id="db.action.office" />
            </Typography>
            {/* The trail matters more than the message: it says which phase
                never finished, which is the one thing a blank start-up would
                otherwise keep to itself. The version belongs beside it: whether
                a slow open is the schema bump of a fresh update or something
                else depends on which Fassung the device is running, and that is
                the one thing nobody can read off a screen the app never got
                past. */}
            <Typography
                component="pre"
                variant="caption"
                color="text.disabled"
                sx={{ maxWidth: TEXT_WIDTH, whiteSpace: 'pre-wrap', m: 0 }}
            >
                {message ? `${message}\n\n` : ''}
                {`${intl.formatMessage(
                    { id: 'db.trace.version' },
                    { version: __APP_VERSION__ }
                )}\n`}
                {formatBootTrace(readBootTrace())}
                {previousTrace
                    ? `\n\n${intl.formatMessage(
                          { id: 'db.trace.previousStart' },
                          { trace: previousTrace }
                      )}`
                    : ''}
            </Typography>
        </Box>
    );
}

/**
 * The server was restored to an earlier state, so this device's copy has to go.
 * One button, not a question - keeping the local copy is not on offer (see
 * utils/dataEpoch.ts). Still shown rather than done silently, because the wipe
 * can take unsent field work with it.
 */
function DataEpochNotice({ onConfirm }: { onConfirm: () => void }) {
    return (
        <Box sx={SCREEN_SX}>
            <Typography variant="h5" component="h2">
                <FormattedMessage id="db.epoch.heading" />
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: TEXT_WIDTH }}>
                <FormattedMessage id="db.epoch.body" />
            </Typography>
            <Button variant="contained" onClick={onConfirm}>
                <FormattedMessage id="db.epoch.confirm" />
            </Button>
        </Box>
    );
}

/** The open rejected - there is an error, and waiting will not help. */
function DatabaseError({
    message,
    reported
}: {
    message: string;
    reported: ReportState | null;
}) {
    return (
        <DatabaseNotice
            heading={<FormattedMessage id="db.failed.heading" />}
            message={message}
            reported={reported}
        >
            <FormattedMessage id="db.failed.body" />
        </DatabaseNotice>
    );
}

/**
 * The open is still running and has simply taken too long to keep quiet about.
 * The wording says so - the app may well start by itself while this is on
 * screen, and telling somebody to reset their data first would be wrong.
 *
 * Unless the database is not slow but HELD: a start that was interrupted while
 * it was opening leaves a connection behind that nothing reaches any more, and
 * then waiting is exactly the wrong advice - so is the reset button, which
 * cannot delete a database it cannot open either. Closing the window is what
 * releases it, and nothing is lost by doing so (see db/openStall.ts).
 */
function DatabaseStalled({
    kind,
    reported
}: {
    kind: StallKind;
    reported: ReportState | null;
}) {
    if (kind === 'blocked') {
        return (
            <DatabaseNotice
                heading={<FormattedMessage id="db.blocked.heading" />}
                reported={reported}
                // A reload never frees a held database - measured against every
                // variant (see db/openStall.ts). Offering it here would be the
                // one button on this screen that cannot work.
                actions={{ reload: false }}
                recovery={
                    canStartNextGeneration() && isOnline()
                        ? {
                              label: (
                                  <FormattedMessage id="db.blocked.continue" />
                              ),
                              run: () => {
                                  startNextDatabaseGeneration();
                                  releaseOpenGuard();
                                  location.reload();
                              },
                              // A staged screen keeps its button but not its
                              // timer: looking must not cost this device a
                              // generation.
                              automaticAfterMs: isStagedFault()
                                  ? undefined
                                  : AUTO_RECOVER_MS
                          }
                        : undefined
                }
            >
                <FormattedMessage id="db.blocked.body" />{' '}
                {!isOnline() ? (
                    <FormattedMessage id="db.blocked.offline" />
                ) : canStartNextGeneration() ? (
                    <FormattedMessage id="db.blocked.canRecover" />
                ) : (
                    <FormattedMessage id="db.blocked.exhausted" />
                )}
            </DatabaseNotice>
        );
    }
    return (
        <DatabaseNotice
            heading={<FormattedMessage id="db.preparing.heading" />}
            // No reload here: doing it mid-start is what leaves the app stuck
            // afterwards, and this screen is the moment people reach for it.
            actions={{ reload: false }}
        >
            <FormattedMessage id="db.preparing.body" />
        </DatabaseNotice>
    );
}

/** Shown only once opening takes long enough to look like nothing happening. */
function DatabaseOpening() {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Typography color="text.secondary">
                <FormattedMessage id="db.opening" />
            </Typography>
        </Box>
    );
}

export function RxDBProvider({ children }: { children: ReactNode }) {
    const [db, setDb] = useState<AppDatabase | null>(null);
    const [error, setError] = useState<Error | null>(null);
    /** The open is taking long enough to be worth showing anything at all. */
    const [slow, setSlow] = useState(false);
    /**
     * …and long enough to offer a way out. The open keeps running regardless.
     * `null` while it is merely running; the kind decides what the screen may
     * ask of the person (see db/openStall.ts).
     */
    const [stalled, setStalled] = useState<StallKind | null>(null);
    /** What became of the report the app filed about it, for the screen. */
    const [reported, setReported] = useState<ReportState | null>(null);
    const { user, sessionExpired } = useAuth();
    const lastUserId = useRef<string | null>(null);
    /** Set the moment the open settles, so a late verdict stays unspoken. */
    const openedMeanwhile = useRef(false);

    // The server is serving different data than this device synced with - it
    // was restored from a backup. Set from the startup check and from the live
    // stream, and it takes over the screen either way.
    const [restoredEpoch, setRestoredEpoch] = useState<string | null>(null);

    // A restore noticed while the app is running: the server was stopped for
    // it, so the stream reconnects afterwards and reports the new epoch.
    useEffect(() => onEpochMismatch(setRestoredEpoch), []);

    // Empty the report queue: what could not be sent when it happened goes out
    // as soon as there is a connection and a token. Started HERE rather than
    // deeper in the app, because the reports worth the most are the ones from
    // a start-up that never got past this component.
    useEffect(() => startReportSender(), []);

    useEffect(() => {
        let cancelled = false;
        // A fresh attempt: whatever a previous one decided about itself is over.
        openedMeanwhile.current = false;
        const noticeDelay = isStagedFault()
            ? // A screen somebody asked to see should not keep them waiting.
              STAGED_NOTICE_MS
            : hasFreshAbandonedOpen()
              ? BLOCKED_NOTICE_MS
              : STALL_NOTICE_MS;

        // Before the local database is opened at all: if the server was
        // restored, this copy is not a cache of it any more (utils/dataEpoch).
        // It runs alongside the open rather than in front of it - the answer
        // may never come, and a device with no signal must still start.
        void checkDataEpoch().then(({ action, serverEpoch }) => {
            if (!cancelled && action === 'reset' && serverEpoch) {
                setRestoredEpoch(serverEpoch);
            }
        });

        // An open that neither resolves nor rejects is the failure mode with no
        // symptom. It gets a screen - but the open is left running, because the
        // same silence is what a merely slow start looks like (STALL_NOTICE_MS).
        const stall = window.setTimeout(
            () => {
                if (cancelled) {
                    return;
                }
                markBoot('db:stalled');
                // Slow or held? The screen and the report say opposite things
                // about what to do, so ask the storage before either appears.
                void classifyStall().then((kind) => {
                    // Two probes take up to six seconds; a database that came
                    // through meanwhile must not be reported as blocked.
                    if (cancelled || openedMeanwhile.current) {
                        return;
                    }
                    console.warn(
                        `[RxDB] die lokale Datenbank ist nach ${noticeDelay / 1000} s noch nicht offen (${kind})`
                    );
                    setStalled(kind);
                    // Reported without being asked: a start-up that hangs is
                    // precisely the fault nobody reports, because the app it
                    // would be reported from is the one that will not start.
                    setReported('sending');
                    void reportDatabaseProblem(
                        kind === 'blocked' ? 'blocked' : 'stalled',
                        ''
                    ).then(setReported);
                });
            },
            // A start that was interrupted mid-open a few minutes ago is the
            // one case where waiting is pointless: say so in seconds, not after
            // half a minute of a spinner that means nothing.
            noticeDelay
        );
        const spinner = window.setTimeout(() => {
            if (!cancelled) {
                setSlow(true);
            }
        }, SPINNER_DELAY_MS);

        /**
         * The waiting is over, whichever way it ended. Both timers ask "is it
         * open YET?" and both must be called off the moment the answer is yes,
         * not only on unmount: left running, a start that took two seconds still
         * reported itself stalled twenty-five seconds later - including a
         * `db:stalled` in the boot trace, which exists to say when a start
         * really did hang.
         */
        const stopWaiting = () => {
            window.clearTimeout(stall);
            window.clearTimeout(spinner);
        };

        getDatabase()
            .then((database) => {
                markBoot('db:ready');
                openedMeanwhile.current = true;
                stopWaiting();
                if (cancelled) {
                    return;
                }
                // It came through after all - nothing to recover from.
                setStalled(null);
                setDb(database);
            })
            .catch((err: unknown) => {
                markBoot('db:failed');
                openedMeanwhile.current = true;
                stopWaiting();
                // Logged as well as shown: the recovery screen says what to do,
                // the console says what happened.
                console.error('[RxDB] open failed', err);
                if (!cancelled) {
                    const failure =
                        err instanceof Error ? err : new Error(String(err));
                    setError(failure);
                    setReported('sending');
                    void reportDatabaseProblem('failed', failure.message).then(
                        setReported
                    );
                }
            });

        return () => {
            cancelled = true;
            stopWaiting();
        };
    }, []);

    /**
     * Close the database when the page really goes away. RxDB's multi-instance
     * cleanup hangs off an `unload` listener, which Chrome now REFUSES to
     * register ("Permissions policy violation"), so a closing window can leave
     * its connection behind and the next start finds the database blocked. Hence
     * `pagehide`, the event that replaced `unload`.
     *
     * NOT when the page goes into the bfcache (`persisted`): it can come back,
     * and it would come back with a closed database.
     */
    useEffect(() => {
        if (!db) {
            return;
        }
        const onPageHide = (event: PageTransitionEvent) => {
            if (!event.persisted) {
                void db.close();
            }
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, [db]);

    // (Re)start replication whenever the user changes and there's no expired session
    useEffect(() => {
        if (!db) {
            return;
        }
        if (!user || sessionExpired) {
            return;
        }
        if (lastUserId.current === user.id) {
            return;
        }

        lastUserId.current = user.id;
        markBoot('replication');
        setupReplication(db);
        startMediaUploader();
        // Signed in, so this device is in use — the moment to ask that its
        // data and photos are not cleared under storage pressure.
        void requestPersistentStorage();
    }, [db, user, sessionExpired]);

    // Keep the "ausstehend" queue honest. Its entries are dropped when a push
    // accepts them, and a document the SERVER refuses - a genuine conflict, or a
    // write the account was not allowed to make - never comes back through that
    // path, so its entry would sit there for good. This sweeps them out in the
    // window that syncs, without anybody having to notice (see pendingQueue).
    useEffect(() => {
        if (!db || !user || sessionExpired) {
            return;
        }
        return startQueueSelfHeal(db);
    }, [db, user, sessionExpired]);

    // Ahead of everything else, including a failed open: whatever state the
    // local database is in, it is the wrong data now.
    if (restoredEpoch) {
        return (
            <DataEpochNotice
                onConfirm={() => {
                    // Accepted BEFORE the wipe - the wipe reloads, and a reload
                    // still holding the old epoch would decide to wipe again.
                    markEpochAccepted(restoredEpoch);
                    void resetLocalData();
                }}
            />
        );
    }

    if (error) {
        return <DatabaseError message={error.message} reported={reported} />;
    }

    if (!db) {
        // A blank page is what a crash looks like, so once the open is slow
        // enough to notice, say that something is happening - and once it is
        // slow enough to doubt, offer the way out without giving up on it.
        if (stalled) {
            return <DatabaseStalled kind={stalled} reported={reported} />;
        }
        return slow ? <DatabaseOpening /> : null;
    }

    return <RxDatabaseProvider database={db}>{children}</RxDatabaseProvider>;
}
